// Tenant white labeling, runtime side.
//
// The backend resolves the workspace from the HOSTNAME (GET /branding) - the
// SPA never asks for a tenant by id here - so abc.hrms.work can only ever
// receive abc's branding. This module:
//   1. applies the last known branding for this host synchronously at boot
//      (no flash of the default title/colours on reload),
//   2. fetches the current branding and applies it,
//   3. exposes the resolved values (with Manitham HRMS defaults filled in)
//      to the login page, app shell and sidebar via useBranding().
import { create } from "zustand";

import { brandingApi, type PublicBranding } from "@/lib/api/branding";
import { applyThemeColors, primaryButtonHex } from "./theme";

export const DEFAULT_PRODUCT_NAME = "Manitham HRMS";
export const DEFAULT_LOGO_SRC = "/manitham-hrms-logo.png";
export const DEFAULT_FAVICON_SRC = "/manitham-hrms-logo.png";
const DEFAULT_THEME_COLOR =
  typeof document !== "undefined"
    ? document.querySelector('meta[name="theme-color"]')?.getAttribute("content") ?? ""
    : "";

/** Branding with every default filled in - what components render. */
export interface ResolvedBranding {
  configured: boolean;
  companyName: string;
  browserTitle: string;
  logoSrc: string;
  /** The uploaded white-label logo only ("" when none) - lets the app shell
   * prefer it over the workspace's own Company Settings logo. */
  customLogoSrc: string;
  /** The white-label display name only ("" when none). */
  customCompanyName: string;
  faviconSrc: string;
  loginTitle: string;
  loginSubtitle: string;
  primaryColor: string;
  secondaryColor: string;
  loginBackgroundColor: string;
  loginBackgroundImage: string;
  supportEmail: string;
  supportPhone: string;
}

type BrandingFields = Pick<
  PublicBranding,
  | "browser_title"
  | "company_display_name"
  | "logo_url"
  | "favicon_url"
  | "login_title"
  | "login_subtitle"
  | "primary_color"
  | "secondary_color"
  | "login_background_color"
  | "login_background_image_url"
  | "support_email"
  | "support_phone"
> & { configured?: boolean };

/**
 * Fills in the defaults. A missing title follows the company name
 * ("ABC Technologies" -> "ABC Technologies HRMS"); with neither set the
 * product's own name is used.
 */
export function resolveBranding(b?: Partial<BrandingFields> | null): ResolvedBranding {
  const companyName = b?.company_display_name?.trim() || "";
  return {
    configured: Boolean(b?.configured ?? companyName),
    companyName: companyName || DEFAULT_PRODUCT_NAME,
    browserTitle: b?.browser_title?.trim() || (companyName ? `${companyName} HRMS` : DEFAULT_PRODUCT_NAME),
    logoSrc: b?.logo_url || DEFAULT_LOGO_SRC,
    customLogoSrc: b?.logo_url || "",
    customCompanyName: companyName,
    faviconSrc: b?.favicon_url || DEFAULT_FAVICON_SRC,
    loginTitle: b?.login_title?.trim() || "",
    loginSubtitle: b?.login_subtitle?.trim() || "",
    primaryColor: b?.primary_color || "",
    secondaryColor: b?.secondary_color || "",
    loginBackgroundColor: b?.login_background_color || "",
    loginBackgroundImage: b?.login_background_image_url || "",
    supportEmail: b?.support_email || "",
    supportPhone: b?.support_phone || "",
  };
}

interface BrandingState {
  branding: ResolvedBranding;
  /** brandingSignature of what is applied - tells a real change from a re-fetch. */
  applied: string;
  /** A newer branding found while this page was open, waiting for
   * BrandingUpdateWatcher to apply it (`next: null` = back to the defaults). */
  pending: { next: PublicBranding | null } | null;
  setBranding: (b: PublicBranding | null) => void;
  setPending: (b: PublicBranding | null) => void;
}

/** What a branding renders as - two equal signatures look identical. */
export function brandingSignature(b: PublicBranding | null | undefined): string {
  if (!b?.configured) return "default";
  return JSON.stringify([
    b.browser_title, b.company_display_name, b.logo_url, b.favicon_url, b.login_title, b.login_subtitle,
    b.primary_color, b.secondary_color, b.login_background_color, b.login_background_image_url,
    b.support_email, b.support_phone,
  ]);
}

export const useBrandingStore = create<BrandingState>((set) => ({
  branding: resolveBranding(null),
  applied: "default",
  pending: null,
  setBranding: (b) => {
    const resolved = resolveBranding(b?.configured ? b : null);
    applyDocumentBranding(resolved);
    set({ branding: resolved, applied: brandingSignature(b), pending: null });
  },
  setPending: (b) => set({ pending: { next: b } }),
}));

/** The current host's branding, defaults filled in. */
export function useBranding(): ResolvedBranding {
  return useBrandingStore((s) => s.branding);
}

/** Writes title, favicon, theme-color and brand colours into the document. */
export function applyDocumentBranding(b: ResolvedBranding, doc: Document = document) {
  doc.title = b.browserTitle;

  // Every rel="icon" link, so a browser can't keep using a stale extra one.
  const icons = Array.from(doc.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'));
  if (icons.length === 0) {
    const link = doc.createElement("link");
    link.rel = "icon";
    doc.head.appendChild(link);
    icons.push(link);
  }
  for (const link of icons) {
    // Uploaded favicons are content-addressed (a new file = a new URL), so
    // changing href is enough to defeat the browser's favicon cache.
    if (link.getAttribute("href") !== b.faviconSrc) link.setAttribute("href", b.faviconSrc);
    link.removeAttribute("type");
  }

  const themeMeta = doc.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (themeMeta) themeMeta.content = primaryButtonHex(b.primaryColor) || DEFAULT_THEME_COLOR;
  const appName = doc.querySelector<HTMLMetaElement>('meta[name="application-name"]');
  if (appName) appName.content = b.browserTitle;
  else {
    const meta = doc.createElement("meta");
    meta.name = "application-name";
    meta.content = b.browserTitle;
    doc.head.appendChild(meta);
  }

  applyThemeColors(b.primaryColor, b.secondaryColor, doc.documentElement);
}

// ---- per-host cache (a convenience only; the server is the source of truth)

const cacheKey = () => `hrms.branding:${window.location.host}`;

function readCache(): PublicBranding | null {
  try {
    const raw = window.localStorage.getItem(cacheKey());
    return raw ? (JSON.parse(raw) as PublicBranding) : null;
  } catch {
    return null;
  }
}

function writeCache(b: PublicBranding | null) {
  try {
    if (b?.configured) window.localStorage.setItem(cacheKey(), JSON.stringify(b));
    else window.localStorage.removeItem(cacheKey());
  } catch {
    // storage blocked (private mode etc.) - nothing to do
  }
}

/** Fetches this host's branding and applies it. Safe to call again. */
export async function refreshBranding(): Promise<void> {
  try {
    const fresh = await brandingApi.publicForHost();
    if (!fresh) return; // network/API failure: keep whatever is showing
    writeCache(fresh);
    useBrandingStore.getState().setBranding(fresh);
  } catch {
    // keep the cached/default branding
  }
}

let lastCheck = 0;
const MIN_CHECK_GAP_MS = 10_000;

/**
 * Re-fetches this host's branding while the page stays open and, when a
 * Super Admin has changed it, queues it as `pending` rather than swapping it
 * under the user - BrandingUpdateWatcher applies it once they are free.
 * Returns whether a change was found. Calls closer together than 10s (tab
 * focus and visibility often fire together) are skipped.
 */
export async function checkForBrandingUpdate(now: number = Date.now()): Promise<boolean> {
  if (now - lastCheck < MIN_CHECK_GAP_MS) return false;
  lastCheck = now;
  try {
    const fresh = await brandingApi.publicForHost();
    if (!fresh) return false;
    writeCache(fresh);
    const state = useBrandingStore.getState();
    const target = state.pending ? brandingSignature(state.pending.next) : state.applied;
    if (brandingSignature(fresh) === target) return false;
    if (brandingSignature(fresh) === state.applied) {
      useBrandingStore.setState({ pending: null }); // changed back before it was applied
      return false;
    }
    state.setPending(fresh.configured ? fresh : null);
    return true;
  } catch {
    return false;
  }
}

/** Applies a queued branding now (no page reload, so nothing typed is lost). */
export function applyPendingBranding() {
  const { pending, setBranding } = useBrandingStore.getState();
  if (pending) setBranding(pending.next);
}

/** Test hook: lets the next checkForBrandingUpdate run immediately. */
export function resetBrandingCheckThrottle() {
  lastCheck = 0;
}

/** Called once from main.tsx before the first render. */
export function bootstrapBranding() {
  // With nothing cached, leave index.html alone until the fetch answers: its
  // favicon link already points at the host-resolved /branding/favicon, and
  // resetting it to the stock icon first would flash the wrong one.
  const cached = readCache();
  if (cached) useBrandingStore.getState().setBranding(cached);
  void refreshBranding();
}
